import React, { useEffect, useLayoutEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const STEPS = [
  { selector: '[data-testid="edit-profile-btn"]', title: "Your profile", body: "Update your logo, location, description and website here at any time — changes go live on your public listing instantly." },
  { selector: '[data-testid="claimed-checklist"]', fallback: '[data-testid="tab-listings"]', title: "Your checklist", body: "Three quick steps take your listing from claimed to polished. Tick them off whenever you have a minute." },
  { selector: '[data-testid="tab-rfqs"]', title: "RFQ Inbox", body: "Quote requests from hotels land here (and in your email). Mark them replied so we know you're on it." },
];

const find = (s) => (s.selector && document.querySelector(s.selector)) || (s.fallback && document.querySelector(s.fallback));

// 3-step pointer tour shown once on the first dashboard visit after activation (server flag welcome_tour_pending).
export default function WelcomeTour({ onDone }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[i];

  useLayoutEffect(() => {
    const el = find(step);
    if (!el) { if (i < STEPS.length - 1) setI(i + 1); else finish(); return; }
    el.scrollIntoView({ block: "center", behavior: "instant" });
    const measure = () => setRect(el.getBoundingClientRect());
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  const finish = async () => {
    try { await api.post("/supplier/welcome-tour/seen"); } catch { /* ignore */ }
    onDone?.();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!rect) return null;
  const pad = 8;
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(340, vw - 32);
  const below = rect.bottom + pad + 220 < vh;
  const top = below ? rect.bottom + pad + 12 : Math.max(16, rect.top - pad - 12 - 200);
  const left = Math.min(Math.max(16, rect.left), vw - cardW - 16);

  return (
    <div className="fixed inset-0 z-[70]" data-testid="welcome-tour" data-step={i + 1}>
      <div className="absolute pointer-events-none rounded-sm transition-all duration-300" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(17,33,26,0.62), 0 0 0 2px #B8860B" }} data-testid="welcome-tour-spotlight" />
      <div className="absolute bg-white shadow-2xl p-5" style={{ top, left, width: cardW }} data-testid="welcome-tour-card">
        <button type="button" onClick={finish} className="absolute top-3 right-3 text-[#7D8C85] hover:text-[#11211A]" aria-label="Skip tour" data-testid="welcome-tour-skip"><X className="w-4 h-4" /></button>
        <div className="tracking-overline text-[10px] text-[#B35C4A]">Quick tour · {i + 1} of {STEPS.length}</div>
        <div className="font-serif text-xl text-[#11211A] mt-1" data-testid="welcome-tour-title">{step.title}</div>
        <p className="text-sm text-[#4A5550] mt-2 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">{STEPS.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${k === i ? "bg-[#264839]" : "bg-[#C4CFC9]"}`} />)}</div>
          <div className="flex gap-2">
            {i > 0 && <button type="button" onClick={() => setI(i - 1)} className="btn-ghost text-xs" data-testid="welcome-tour-back">Back</button>}
            {i < STEPS.length - 1
              ? <button type="button" onClick={() => setI(i + 1)} className="btn-primary text-xs" data-testid="welcome-tour-next">Next</button>
              : <button type="button" onClick={finish} className="btn-primary text-xs" data-testid="welcome-tour-finish">Got it</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
